import { useEffect, useState } from "react";
import { X, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Phase 1 manual bill grouping — confirmation sheet.
 *
 * Renders a compact list of the selected source candidates with
 * up/down controls to establish page order. Fires the group endpoint
 * on confirmation and reports the resulting grouped candidate id
 * upward so the queue can refresh + optionally navigate.
 */
export default function GroupBillModal({ open, candidates, onClose, onGrouped }) {
  const [order, setOrder] = useState([]);
  const [busy, setBusy] = useState(false);

  // Reset the ordered id list every time the modal opens. Using a
  // lazy useState initializer here would capture `candidates` only
  // on the first mount (when the queue renders with nothing
  // selected), leaving `order = []` even after the operator picks
  // rows — the modal would then show "Group as one 0-page bill?"
  // and submit page_order=[].
  useEffect(() => {
    if (open) setOrder(candidates.map((c) => c.id));
  }, [open, candidates]);

  if (!open) return null;

  const byId = Object.fromEntries(candidates.map((c) => [c.id, c]));
  const move = (idx, delta) => {
    const next = [...order];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setOrder(next);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.groupCandidates({
        candidate_ids: candidates.map((c) => c.id),
        page_order: order,
      });
      toast.success(`Grouped ${r.page_count} pages into one bill`);
      onGrouped?.(r);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object") ? detail.message : (detail || "Grouping failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="group-modal"
    >
      <div className="w-full max-w-md bg-white border border-archival rounded-md shadow-xl">
        <div className="px-5 py-3 border-b border-archival flex items-center justify-between">
          <div>
            <div className="text-[15px] font-semibold text-archival-primary">
              Group as one {order.length}-page bill?
            </div>
            <div className="text-[11px] text-archival-muted mt-0.5">
              OCR will re-run across the ordered pages.
            </div>
          </div>
          <button onClick={onClose} data-testid="group-modal-close"
                  className="text-archival-muted hover:text-archival-primary">
            <X size={16} />
          </button>
        </div>
        <ul className="px-3 py-3 space-y-1.5 max-h-[50vh] overflow-y-auto">
          {order.map((id, idx) => {
            const c = byId[id];
            return (
              <li
                key={id}
                data-testid={`group-modal-page-${idx + 1}`}
                className="flex items-center gap-2 px-2 py-2 rounded-sm border border-archival bg-archival-soft"
              >
                <GripVertical size={14} className="text-archival-muted flex-shrink-0" />
                <span className="text-[11px] font-mono uppercase tracking-wider text-archival-secondary">
                  Page {idx + 1}
                </span>
                <span className="text-[12px] text-archival-primary truncate flex-1" title={c?.original_filename}>
                  {c?.original_filename || id.slice(0, 8)}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    data-testid={`group-modal-up-${idx}`}
                    className="p-1 text-archival-muted hover:text-archival-primary disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Move up"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    disabled={idx === order.length - 1}
                    onClick={() => move(idx, 1)}
                    data-testid={`group-modal-down-${idx}`}
                    className="p-1 text-archival-muted hover:text-archival-primary disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Move down"
                  >
                    <ArrowDown size={12} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="px-5 py-3 border-t border-archival flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="group-modal-cancel"
                  className="border-archival h-8 text-[12px]">
            Cancel
          </Button>
          <Button onClick={confirm} disabled={busy} data-testid="group-modal-confirm"
                  className="bg-archival-primary text-white h-8 text-[12px]">
            {busy ? "Grouping…" : "Group as one bill"}
          </Button>
        </div>
      </div>
    </div>
  );
}
